import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  NumberInput,
  Dialog,
  FormField,
  Grid,
  Heading,
  InputGroup,
  ControlGroup,
  majorScale,
  EmptyInputGroup,
} from '../../npaper';
import { CurrencySelect } from '../../npaper/CurrencySelect';
import {
  IProduct,
  ISupplierItem,
  ISupplierItemSnapshotIn,
  useStores,
} from '../../stores';
import { CompanySelect } from '../companies/CompanySelect';

export const SupplierProductDialog: React.FC<{
  isShown: boolean;
  onClose: (item?: ISupplierItem) => void;
  supplierProductItem?: ISupplierItem;
  product: IProduct;
}> = ({ onClose, supplierProductItem, product, ...props }) => {
  const [isShown, setIsShown] = useState(props.isShown);
  const [created, setCreated] = useState<ISupplierItem>();
  useEffect(() => {
    setIsShown(props.isShown);
  }, [props.isShown]);

  const close = () => {
    setIsShown(false);
  };

  const { supplierItemStore: supplierProductItemStore, productStore } =
    useStores();
  const {
    register,
    control,
    errors,
    setValue,
    handleSubmit,
    formState,
    reset,
  } = useForm<ISupplierItemSnapshotIn>();

  useEffect(() => {
    if (!isShown) return;
    if (supplierProductItem) {
      reset(getSnapshot(supplierProductItem));
    } else {
      reset({
        cost_price: 0,
        stock: 0,
        supplier_number: product && product.sku ? product.sku : '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.barcode, supplierProductItem, isShown]);

  const onSubmit = async (values: ISupplierItemSnapshotIn) => {
    if (supplierProductItem) {
      try {
        await supplierProductItemStore.updateItem(supplierProductItem, values);
        close();
      } catch (err) {
        // TODO: Handle error
        throw err;
      }
    } else {
      try {
        const item = await supplierProductItemStore.createItem({
          ...values,
          product: product.id,
        });
        await productStore.getItem(product.id, true);
        setCreated(item);
        close();
      } catch (err) {
        // TODO: Handle error
        throw err;
      }
    }
  };

  return (
    <Dialog
      title={
        supplierProductItem
          ? 'Rediger leverandørs vare'
          : 'Tilføj leverandørs vare'
      }
      isShown={isShown}
      onClose={() => onClose(created)}
      actions={
        <>
          <Button onClick={() => close()} disabled={formState.isSubmitting}>
            Annuller
          </Button>
          <Button
            primary
            isLoading={formState.isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            {supplierProductItem ? 'Gem ændringer' : 'Tilføj leverandør'}
          </Button>
        </>
      }
    >
      <Grid padding={majorScale(2)}>
        <Heading size={300} className="col-span-12">
          Detaljer
        </Heading>

        <FormField
          className="col-span-9"
          label="Leverandør"
          inline={true}
          hint={errors.supplier && errors.supplier.message}
          isInvalid={!!errors.supplier}
        >
          <Controller
            control={control}
            name="supplier"
            rules={{ required: 'Du skal vælge en leverandør' }}
            defaultValue={null}
            render={({ onChange, value }) => (
              <CompanySelect
                onSelect={(company) => {
                  onChange(company.id);
                  setValue('currency', company.currency);
                }}
                selected={value}
              />
            )}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label="Vare nr."
          hint="Skriv leverandørens varenummer så denne kan genkende en eventuel bestilling fra dig"
          inline={true}
        >
          <InputGroup
            placeholder="Leverandørs vare nr."
            name="supplier_number"
            validationMessage={
              errors.supplier_number && errors.supplier_number.message
            }
            isInvalid={!!errors.supplier_number}
            ref={register({
              required:
                'Du skal skrive leverandørens varenummer - ellers kan vi ikke lave en bestilling',
            })}
          />
        </FormField>

        {/*<FormField
          className="col-span-9"
          label="Stregkode"
          hint="Vælg eventuelt din leverandørs stregkode"
          inline={true}
        >
          <InputGroup
            placeholder="Stregkodens værdi"
            name="barcode.value"
            ref={register}
          />
          <Controller
            control={control}
            name="barcode.type"
            defaultValue={null}
            render={({ onChange, value }) => (
              <Select
                placeholder="Vælg type på stregkode"
                onSelect={(v) => onChange(v.id)}
                value={value}
                items={Object.values(BarcodeType).map((b) => ({
                  id: b,
                }))}
              />
            )}
          />
        </FormField>*/}

        <FormField
          className="col-span-9"
          label="Kostpris"
          hint="Angiv leverandørens kostpris - det hjælper dig sammesætte de tilbud der giver dig bedste resultat"
          inline={true}
        >
          <ControlGroup>
            <Controller
              control={control}
              name="cost_price"
              rules={{ required: 'Du skal andgive en kostpris' }}
              render={({ onChange, value, onBlur }) => (
                <NumberInput
                  placeholder="Leverandørens pris for varen"
                  onChange={onChange}
                  value={value}
                  onBlur={onBlur}
                />
              )}
            />
            <Controller
              control={control}
              name="cost_price_currency"
              defaultValue="DKK"
              render={({ onChange, value }) => (
                <CurrencySelect value={value} onChange={onChange} />
              )}
            />
          </ControlGroup>
        </FormField>

        <FormField
          className="col-span-9"
          label="Lager"
          hint="Angiv leerandørens lagerstatus - det hjælper dig finde varer som du kan levere hurtigt"
          inline={true}
        >
          <ControlGroup>
            <Controller
              control={control}
              name="stock"
              render={({ onChange, value, onBlur }) => (
                <NumberInput
                  placeholder="Leverandørens lagerstatus"
                  onChange={onChange}
                  value={value}
                  onBlur={onBlur}
                />
              )}
            />
            <EmptyInputGroup>stk</EmptyInputGroup>
          </ControlGroup>
        </FormField>
      </Grid>
    </Dialog>
  );
};
